import {
  Component, DestroyRef, inject, OnDestroy, OnInit, PLATFORM_ID, signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { IdleService } from '../../../core/services/idle.service';
import { AuthService } from '../../../core/auth/auth.service';
import { environment } from '../../../../environments/environment';

/**
 * App-wide idle-timeout popup. Lives in the authenticated layout, so it covers
 * the entire application once a user is logged in.
 *
 * When {@link IdleService} reports inactivity, this shows a modal countdown.
 * "Stay signed in" resets the idle watch; "Logout" (or the countdown reaching
 * zero) signs the user out.
 */
@Component({
  selector: 'app-idle-timeout',
  standalone: true,
  imports: [Dialog, Button],
  templateUrl: './idle-timeout.component.html',
  styleUrls: ['./idle-timeout.component.css'],
})
export class IdleTimeoutComponent implements OnInit, OnDestroy {
  private idle       = inject(IdleService);
  private auth       = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);

  visible   = signal(false);
  countdown = signal(0);

  private readonly countdownSec = environment.session.idleCountdownSec;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.idle.start();
    this.idle.idleStart$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.showWarning());
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.idle.stop();
  }

  /** "Stay signed in" — dismiss the popup and resume watching. */
  stay(): void {
    this.clearCountdown();
    this.visible.set(false);
    this.idle.reset();
  }

  /** "Logout" or countdown expiry — sign the user out. */
  logout(): void {
    this.clearCountdown();
    this.visible.set(false);
    this.idle.stop();
    this.auth.signOut();
  }

  private showWarning(): void {
    this.countdown.set(this.countdownSec);
    this.visible.set(true);
    this.clearCountdown();
    this.countdownTimer = setInterval(() => {
      const next = this.countdown() - 1;
      this.countdown.set(next);
      if (next <= 0) this.logout();
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
